import { useHasHover } from '@lib/hooks/sizing/useHasHover';
import { debugStore } from '@stores/Debug';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** A new card must be this much closer (px) than the current one to take over */
const HYSTERESIS = 50;

/**
 * The center line starts at the top of the viewport so the first card is
 * always "centered", eases to true center (50%) as the user scrolls, then
 * eases back toward the bottom (100%) near the end of the scroll so the
 * last card can also be "centered".
 *
 * TOP_RATIO / BOTTOM_RATIO: where the line sits at the scroll extremes
 * MID_RATIO: where it settles in the middle (true center)
 * TRANSITION_PX: scroll distance over which each ease completes
 */
const TOP_RATIO = 0;
const MID_RATIO = 0.5;
const BOTTOM_RATIO = 1;
const TRANSITION_PX = 300;

type Picked = {
  /**
   * The card nearest the centre line — the geometric anchor the band is measured from. Null on a
   * hover device, or before the first pick.
   *
   * Not the right thing to reveal controls with (use `bandIds`) or to play video with (use
   * `playbackId`); it is the input both are derived from.
   */
  centeredId: string | null;
  /**
   * Every card level with the centered one — its row — including the centered card itself.
   *
   * This is what reveals a card's controls. `centeredId` addresses one card, which is all a single
   * column needs, but in a multi-column grid the rest of the row has no hover to fall back on.
   */
  bandIds: ReadonlySet<string>;
};

const NOTHING_PICKED: Picked = { centeredId: null, bandIds: new Set() };

type CenteredCardContextValue = Picked & {
  /**
   * The one card that may autoplay, or null when no single card is nominated.
   *
   * Deliberately narrower than `bandIds`: revealing a row of controls is fine, but playing a row of
   * videos is not, and with several cards revealed there is no non-arbitrary way to choose one. So
   * autoplay happens only where exactly one card is revealed — which is the single-column centre
   * line, and nothing else. A hover device doesn't come through here at all; the cursor names the
   * card and VideoPreview plays it directly.
   */
  playbackId: string | null;
  /** Register a card element to be tracked. Returns an unregister function. */
  register: (id: string, el: HTMLElement) => () => void;
  /**
   * Whether this centre-line model is the live hover substitute for the grid below — true exactly
   * when the device can't hover, at any viewport width.
   *
   * Cards need this as well as `bandIds`: "nothing is in the band right now" and "this grid
   * isn't using the centre line" both read as an empty set, but the first means show nothing and
   * the second means fall back to hover.
   */
  isActive: boolean;
};

const CenteredCardContext = createContext<CenteredCardContextValue>({
  ...NOTHING_PICKED,
  playbackId: null,
  register: () => () => {},
  isActive: false,
});

/** Ease-out quadratic: fast start, smooth deceleration. */
function easeOutQuad(t: number): number {
  return 1 - (1 - t) * (1 - t);
}

/** Compute the dynamic center-line Y position based on scroll progress. */
function getCenterY(scrollTop: number, maxScroll: number): number {
  const vh = window.innerHeight;

  // Ease from top → center (0 → 0.5vh)
  const topProgress = Math.min(scrollTop / TRANSITION_PX, 1);
  const fromTop = vh * (TOP_RATIO + (MID_RATIO - TOP_RATIO) * easeOutQuad(topProgress));

  // Ease from center → bottom (0.5vh → vh)
  if (maxScroll <= 0) return fromTop;
  const distFromBottom = maxScroll - scrollTop;
  const bottomProgress = Math.min(distFromBottom / TRANSITION_PX, 1);
  const fromBottom = vh * (BOTTOM_RATIO + (MID_RATIO - BOTTOM_RATIO) * easeOutQuad(bottomProgress));

  // fromTop: 0 → 0.5vh, fromBottom: 0.5vh → vh
  // Combine so both ends pull away from center:
  // top=0: 0 + (0.5vh - 0.5vh) = 0, mid: 0.5vh + 0 = 0.5vh, bottom: 0.5vh + (vh - 0.5vh) = vh
  return fromTop + fromBottom - vh * MID_RATIO;
}

/** Debug overlay: a horizontal line showing the current center-line position. */
function DebugCenterLine({ centerY }: { centerY: number }) {
  return (
    <>
      {createPortal(
        <div
          style={{
            position: 'fixed',
            top: centerY,
            left: 0,
            right: 0,
            height: 2,
            background: 'rgba(208, 169, 239, 0.7)',
            pointerEvents: 'none',
            zIndex: 99999,
            boxShadow: '0 0 6px rgba(208, 169, 239, 0.5)',
          }}
        />,
        document.body,
      )}
    </>
  );
}

/** Membership comparison, so an identical band doesn't re-render every card in the grid. */
function sameBand(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) if (!b.has(id)) return false;
  return true;
}

export function CenteredCardProvider({ children }: { children: React.ReactNode }) {
  const hasHover = useHasHover();
  /**
   * Keyed on hover capability, never on viewport width. A mouse in a narrow window can hover, and
   * running the centre line alongside a working cursor would pin one card permanently revealed.
   */
  const enabled = !hasHover;
  // One state for both outputs, and a ref mirroring it that `pick` reads for hysteresis. Kept as a
  // single pair so the ref can't drift from the state the way two independently-updated pairs did.
  const [picked, setPicked] = useState<Picked>(NOTHING_PICKED);
  const pickedRef = useRef<Picked>(NOTHING_PICKED);
  const cardsRef = useRef(new Map<HTMLElement, string>());
  const scrollEndTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pickFrame = useRef<number | null>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const [debugCenterY, setDebugCenterY] = useState(0);

  /**
   * The scrollable ancestor the registered cards live in, found by walking up from one of them.
   *
   * Geometry is READ from this on demand rather than pushed in by the scroll handler, which is what
   * lets a pick be correct before any scrolling has happened — a handler-fed `maxScroll` starts at 0
   * and reads as "this grid can't scroll", which is a very different thing from "not measured yet".
   * It also means an unrelated scroller can't donate its geometry to this grid.
   *
   * Cached; re-resolved if the element goes away.
   */
  const resolveScroller = useCallback((): HTMLElement | null => {
    const cached = scrollerRef.current;
    if (cached?.isConnected) return cached;
    const firstCard: HTMLElement | undefined = cardsRef.current.keys().next().value;
    for (let el = firstCard?.parentElement; el; el = el.parentElement) {
      const { overflowY } = getComputedStyle(el);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        scrollerRef.current = el;
        return el;
      }
    }
    scrollerRef.current = null;
    return null;
  }, []);

  /** Where the centre line currently sits, derived from the grid's own scroll position. */
  const readCenterY = useCallback((): number => {
    const scroller = resolveScroller();
    if (!scroller) return getCenterY(0, 0);
    return getCenterY(scroller.scrollTop, scroller.scrollHeight - scroller.clientHeight);
  }, [resolveScroller]);

  const pick = useCallback((): Picked => {
    const scroller = resolveScroller();
    const maxScroll = scroller ? scroller.scrollHeight - scroller.clientHeight : 0;
    const centerY = readCenterY();
    const previousId = pickedRef.current.centeredId;
    let bestId: string | null = null;
    let bestDist = Infinity;
    let currentDist = Infinity;
    const spans = new Map<string, { top: number; bottom: number }>();

    cardsRef.current.forEach((id, el) => {
      const rect = el.getBoundingClientRect();
      const cardCenter = rect.top + rect.height / 2;
      const dist = Math.abs(cardCenter - centerY);
      if (dist < bestDist) {
        bestDist = dist;
        bestId = id;
      }
      if (id === previousId) {
        currentDist = dist;
      }
      spans.set(id, { top: rect.top, bottom: rect.bottom });
    });

    // Only switch if the new card is meaningfully closer than the current one
    let nextCentered: string | null = bestId;
    if (previousId && bestId !== previousId && currentDist - bestDist < HYSTERESIS) {
      nextCentered = previousId;
    }

    // A grid shorter than its viewport can't scroll, so the centre line can never be moved off the
    // top and every row but the first would be permanently unreachable. Everything is already on
    // screen at once there, so the band is simply all of it — and because playback keys off
    // `playbackId` rather than the band, a wider band still can't start a second video.
    //
    // `maxScroll` is measured from the container here, not carried in a ref the scroll handler
    // fills: a ref would still be 0 on the first pick and every scrollable grid would briefly
    // reveal every card.
    if (maxScroll <= 0) {
      return { centeredId: nextCentered, bandIds: new Set(spans.keys()) };
    }

    // The band: the centered card plus everything level with it, i.e. its row.
    //
    // "Overlaps the centered card vertically" rather than "the line passes through it", which
    // matters twice. At the scroll extremes the line sits at the very top or bottom of the viewport
    // and can miss every card, which would leave the band empty and the row with no controls. And
    // in a masonry grid a neighbour is not always the same height, so sharing a `top` is too strict
    // a test for "beside it". Adjacent rows are separated by the grid gap, so the strict
    // inequalities keep them out.
    const bandIds = new Set<string>();
    const anchor = nextCentered === null ? undefined : spans.get(nextCentered);
    if (anchor) {
      for (const [id, span] of spans) {
        if (span.top < anchor.bottom && span.bottom > anchor.top) bandIds.add(id);
      }
    }

    return { centeredId: nextCentered, bandIds };
  }, [resolveScroller, readCenterY]);

  /** Run a pick and commit it only if either output actually changed. */
  const applyPick = useCallback(() => {
    const next = pick();
    const previous = pickedRef.current;
    if (next.centeredId === previous.centeredId && sameBand(next.bandIds, previous.bandIds)) return;
    pickedRef.current = next;
    setPicked(next);
  }, [pick]);

  /**
   * Coalesce to one pick per frame. `register` fires as each card mounts, and the grid is
   * virtualised — a sustained scroll mounts cards roughly every 100ms, so picking synchronously per
   * registration ran a full pass over every card several times a second and defeated the
   * scroll-settle debounce below that exists to avoid exactly that.
   */
  const schedulePick = useCallback(() => {
    if (pickFrame.current !== null) return;
    pickFrame.current = requestAnimationFrame(() => {
      pickFrame.current = null;
      applyPick();
    });
  }, [applyPick]);

  // Timers outlive the component that started them; a pending frame must not fire after unmount.
  useEffect(
    () => () => {
      if (pickFrame.current !== null) cancelAnimationFrame(pickFrame.current);
      pickFrame.current = null;
    },
    [],
  );

  useEffect(() => {
    if (!enabled) {
      pickedRef.current = NOTHING_PICKED;
      setPicked(NOTHING_PICKED);
      return;
    }

    const onScroll = (e: Event) => {
      // Nothing registered means nothing this could ever nominate, so don't spend a timer on it. A
      // grid whose cards opt out still mounts the provider, and this listener is on `document` with
      // capture, so without this it schedules a pick for every scroll anywhere in the app.
      if (cardsRef.current.size === 0) return;

      // Only this grid's own scroller moves the centre line. The listener is on `document` with
      // capture, so it sees every scroll in the app — including horizontal-only ones like the
      // library header (`LibraryContent.module.scss`), whose scrollHeight equals its clientHeight
      // and which would otherwise report this grid as unscrollable.
      if (e.target !== resolveScroller()) return;

      // Update debug line position on every scroll (cheap — just a state set)
      if (debugStore.showCenterLine) {
        setDebugCenterY(readCenterY());
      }

      // Debounce: only pick the centered card once scrolling settles
      if (scrollEndTimer.current !== null) clearTimeout(scrollEndTimer.current);
      scrollEndTimer.current = setTimeout(applyPick, 150);
    };

    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    // Rotating a tablet or resizing reflows the grid under a band that was measured against the old
    // layout, and neither fires a scroll event. Without this the revealed row can end up somewhere
    // the centre line no longer is, and on a grid that doesn't scroll nothing would ever correct it.
    window.addEventListener('resize', schedulePick);
    window.addEventListener('orientationchange', schedulePick);
    // Initial pick
    setDebugCenterY(readCenterY());
    applyPick();

    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', schedulePick);
      window.removeEventListener('orientationchange', schedulePick);
      if (scrollEndTimer.current !== null) clearTimeout(scrollEndTimer.current);
    };
  }, [enabled, applyPick, schedulePick, resolveScroller, readCenterY]);

  const register = useCallback(
    (id: string, el: HTMLElement) => {
      cardsRef.current.set(el, id);
      if (enabled) schedulePick();
      return () => {
        cardsRef.current.delete(el);
        // Re-pick on removal too: deleting a media item reflows the grid, and the band would
        // otherwise keep naming cards that have moved or gone.
        if (enabled) schedulePick();
      };
    },
    [enabled, schedulePick],
  );

  return (
    <CenteredCardContext.Provider
      value={{
        centeredId: picked.centeredId,
        bandIds: picked.bandIds,
        // Only when the band is a single card. See `playbackId` on the context type.
        playbackId: picked.bandIds.size === 1 ? picked.centeredId : null,
        register,
        isActive: enabled,
      }}
    >
      {children}
      {enabled && debugStore.showCenterLine && <DebugCenterLine centerY={debugCenterY} />}
    </CenteredCardContext.Provider>
  );
}

export function useCenteredCard() {
  return useContext(CenteredCardContext);
}
